import {Component} from 'panel';
import {registerMPElement} from 'common/util/register-element';
import StagedCohort from 'common/widgets/cohorts-query-builder/models/builder/staged-cohort';
import Cohort from 'common/widgets/cohorts-query-builder/models/cohort';
import cohortStore from 'common/report/stores/cohorts';
import {reporter} from 'common/report/error-reporting';

import template from './index.jade';
import css from './index.styl?inline';
import {SerializedCohort} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';

enum CohortsModalError {
  NameRequired,
}

interface CohortsModalState {
  name: string;
  showCreateEntityModal: boolean;
  loadingCohorts: boolean;
  errors: Set<CohortsModalError>;
}

export default registerMPElement(
  `mp-inline-cohorts-modal`,
  class CohortsModal extends Component<CohortsModalState> {
    public context;
    private _cohortStoreCallback;
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'staged-cohort': `json`,
        'project-id': `number`,
        'workspace-id': `number`,
        'data-group-id': `string`,
        'can-save': `boolean`,
        'project-has-cohorts': `boolean`,
        'is-new-cohort-definition': `boolean`,
        'user-id': `string`,
        'track-name': `string`,
        name: `string`,
        open: `boolean`,
        type: `string`,
      };
    }

    connectedCallback() {
      super.connectedCallback();

      this.fetchCohorts();
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      cohortStore.unsubscribe(this._cohortStoreCallback);
    }

    fetchCohorts() {
      this.update({loadingCohorts: true});

      this._cohortStoreCallback = () => {
        this.update({loadingCohorts: false});
      };

      cohortStore.subscribe(this._cohortStoreCallback);
      cohortStore.fetch({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }

    getDeserializedStagedCohort() {
      return StagedCohort.deserialize(this.attr(`staged-cohort`));
    }

    attributeChangedCallback(attr, oldValue, newValue) {
      super.attributeChangedCallback(attr, oldValue, newValue);

      switch (attr) {
        case `open`:
        case `name`:
          this.update({name: this.attr(`name`)});
          break;
      }
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        defaultState: {
          name: ``,
          showCreateEntityModal: false,
          loadingCohorts: false,
          errors: new Set<CohortsModalError>(),
        },
        helpers: {
          CohortsModalError,
          trackCohortsBuilderEvent: (ev) => {
            const {properties, event} = ev.detail;
            ev.stopPropagation();
            this.trackEvent(event, properties);
          },
          getStagedCohort: () => this.getAttribute(`staged-cohort`),
          getMPContext: () => this.context,
          getCohorts: () =>
            Object.values(cohortStore.value).map((cohort: SerializedCohort) => ({
              createdBy: cohort.created_by,
              lastEdited: cohort.last_edited,
              hidden: !cohort.is_visible,
              locked: cohort.is_locked,
              ...cohort,
            })),
          getCohortNames: () =>
            this.helpers
              .getCohorts()
              .filter((cohort) => Number.isInteger(cohort.id))
              .map(({name}) => name),
          getTitle: () => {
            // Only display title if Modal is open
            if (!this.attr(`open`)) {
              return ``;
            }

            return this.attr(`is-new-cohort-definition`)
              ? `Add cohort ${this.attr(`type`) || ``}`
              : `Update cohort ${this.attr(`type`) || ``}`;
          },
          isSaveDisabled: () => {
            const stagedCohort = this.getDeserializedStagedCohort();
            return !stagedCohort || !stagedCohort.isValid();
          },
          isOpen: () => this.attr(`open`),
          openEntityModal: () => this.update({showCreateEntityModal: true}),
          closeEntityModal: () => this.update({showCreateEntityModal: false}),
          commitStagedCohort: () => {
            const stagedCohort = this.getDeserializedStagedCohort().updateDetails({
              name: this.state.name,
            });

            const {name} = stagedCohort;

            if (!name) {
              return this.addError(CohortsModalError.NameRequired);
            }

            this.dispatchEvent(new CustomEvent(`commitStagedCohort`, {detail: {name}}));
          },
          saveStagedCohort: async (ev) => {
            const cohort = Cohort.createCohortFromStaged(
              this.getDeserializedStagedCohort().updateDetails(ev.detail.value),
            );

            try {
              const {id, name} = await cohortStore.post({
                workspaceId: this.attr(`workspace-id`),
                projectID: this.attr(`project-id`),
                cohort: cohort.serialize(),
              });

              this.dispatchEvent(new CustomEvent(`commitSavedCohort`, {detail: {id, name}}));
            } catch (error) {
              this.helpers.closeModal();
              reporter.error(error);
            }

            this.helpers.closeEntityModal();
          },
          getModalAttributes: () => {
            return {
              open: this.attr(`open`),
              closeable: true,
              'grow-to-content': true,
              'modal-type': `modal`,
              'vertical-alignment': `top`,
              scrollable: true,
            };
          },
          handleNameInputChange: (ev) => {
            const {value} = ev.target;
            this.update({name: value});

            if (value && this.state.errors.has(CohortsModalError.NameRequired)) {
              this.removeError(CohortsModalError.NameRequired);
            }
          },
          handleStagedCohortUpdate: (updatedStagedCohort) => {
            const stagedCohort = this.getDeserializedStagedCohort();
            try {
              // Scroll to bottom when a new literal is added
              if (stagedCohort.literals.length < updatedStagedCohort.literals.length) {
                const el = this.el.querySelector(`mp-modal`).shadowRoot.querySelector(`.mp-modal-scrollable`);
                requestAnimationFrame(() => {
                  el.scrollTop = el.scrollHeight;
                });
              }
            } catch (err) {
              console.warn(`Cannot scroll to the bottom of scrollable element in shadow DOM`, err);
            }

            this.dispatchEvent(
              new CustomEvent(`stagedCohortUpdated`, {
                detail: {
                  stagedCohort: updatedStagedCohort,
                },
              }),
            );
          },
          handleModalStateChange: (ev) => {
            const {state} = ev.detail;

            if (state === `closed`) {
              return this.helpers.closeModal();
            }

            this.dispatchEvent(new CustomEvent(`change`, {detail: {state}}));
          },
          closeModal: () => {
            this.update({errors: new Set()});
            this.dispatchEvent(new CustomEvent(`change`, {detail: {state: `closed`}}));
          },
        },
      };
    }

    private addError(value: CohortsModalError) {
      const errors = new Set([...this.state.errors]);
      errors.add(value);

      this.update({errors});
    }

    private removeError(value: CohortsModalError) {
      const errors = new Set([...this.state.errors]);
      errors.delete(value);

      this.update({errors});
    }

    private trackEvent(eventName, props) {
      this.dispatchEvent(
        new CustomEvent(`track`, {
          bubbles: true,
          composed: true,
          detail: {
            eventName,
            props,
          },
        }),
      );
    }
  },
);
